import classNames from 'classnames';
import { motion } from 'framer-motion';
import React from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import SearchBarSectionMobileWrapper from '@/components/organisms/search-bar/shared/search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './search-bar-area-dropdown.module.scss';

export interface SearchBarAreaDropdownProps {
  /** The available areas to choose from */
  areas?: Content.Components['TravelPortalAvailableArea'][];
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Default number of adults per room */
  description?: string | string[];
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** An event that is triggered on click search */
  onClickSearch?: () => void;
}

const SearchBarAreaDropdown = forwardRef(
  (
    {
      areas = [],
      className,
      description,
      isVisible = false,
      label,
      onClickClose,
      onClickNext,
      onClickSearch,
    }: SearchBarAreaDropdownProps,
    ref,
  ) => {
    const { area, setArea } = useSearchContext();
    const isLaptop = useWindowSize('laptop');

    const { t } = useI18n();

    const handleClickClear = () => {
      setArea('');
    };

    useImperativeHandle(ref, () => ({
      clear: handleClickClear,
    }));

    const handleAreaClick = (
      item:
        | Content.Components['TravelPortalAvailableArea']
        | Content.Components['TravelPortalAvailableLinkArea'],
    ) => {
      setArea(item.id);

      if (item.$type === 'TravelPortalAvailableSearchArea') return;

      if (item.$type === 'TravelPortalAvailableLinkArea') {
        const { target, url } = (
          item as Content.Components['TravelPortalAvailableLinkArea']
        ).link;

        window.open(url, target);
      }
    };

    const renderOptions = () => {
      if (areas.length === 0) return null;

      return (
        <React.Fragment>
          {areas.map((item, itemIndex) => (
            <button
              key={itemIndex}
              className={classNames(styles.button, {
                [styles.isActive]: area === item.id,
              })}
              onClick={() => handleAreaClick(item)}
            >
              <Icon name={item.icon} size={24} />

              <Typography className={styles.text} tagStyle="bodySmall">
                {item.label}
              </Typography>
            </button>
          ))}
        </React.Fragment>
      );
    };

    return (
      <div className={classNames(styles.component, className)}>
        {isLaptop ? (
          <motion.div
            animate={isVisible ? 'show' : 'hidden'}
            className={styles.desktopWrapper}
            exit="hidden"
            initial="hidden"
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            variants={{
              hidden: {
                opacity: 0,
                transitionEnd: {
                  display: 'none',
                },
                translateY: 0,
              },
              show: {
                display: 'block',
                opacity: 1,
                translateY: 0,
              },
            }}
          >
            <div className={styles.content}>
              <div className={styles.dropdownWrapper}>
                <div className={styles.options}>{renderOptions()}</div>
              </div>
            </div>
          </motion.div>
        ) : (
          <SearchBarSectionMobileWrapper
            buttonLabel={t('items.common.words.search')}
            description={description}
            isOpen={isVisible}
            label={label}
            onClickClear={handleClickClear}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            shouldTriggerSearch
            showBackButton
          >
            <div className={styles.mobileWrapper}>
              <div className={styles.optionsWrapper}>
                <Typography
                  className={styles.label}
                  tag="label"
                  tagStyle="labelMedium"
                >
                  {t('items.campaign.searchBar.area.chooseArea')}
                </Typography>

                <div className={styles.options}>{renderOptions()}</div>
              </div>
            </div>
          </SearchBarSectionMobileWrapper>
        )}
      </div>
    );
  },
);

SearchBarAreaDropdown.displayName = 'SearchBarAreaDropdown';

export default SearchBarAreaDropdown;
